import { FaLaptopCode, FaMobileAlt, FaCloud, FaEnvelopeOpenText, FaBrain, FaServer, FaCode, FaPaintBrush, FaSearch, FaRocket } from 'react-icons/fa';

export const ServicesLinks = [
    {
        id: "web-development",
        title: "Web App Platforms",
        url: "/services/web-development",
        description: "From high-impact marketing websites to feature-packed enterprise web applications and custom admin portals. Built using the best modern design patterns.",
        icon: FaLaptopCode,
        gradient: 'from-blue-500 to-cyan-400',
        iconBg: 'bg-blue-50 text-blue-600 dark:bg-blue-950/30 dark:text-blue-400',
        imageName: '/images/web_dev_hero.png',
        tags: ['React & Next.js', 'Node/Express', 'REST & GraphQL'],
        benefits: [
            { icon: FaCode, title: "Modern Design Patterns", text: "We follow industry-standard guidelines to build beautiful, responsive user touchpoints." },
            { icon: FaMobileAlt, title: "Mobile-First Rendering", text: "Flawless rendering and modular structural consistency across all screens." },
            { icon: FaSearch, title: "SEO Centered", text: "Optimized markup and asset delivery ensuring ultra-fast loading speed and visibility." },
            { icon: FaRocket, title: "Scalable Core", text: "We structuralize databases and APIs prepared for concurrent demand growth from day one." }
        ],
        processSteps: [
            { title: "Discovery & Structure", desc: "Understanding database relations, wireflows, and client specifications." },
            { title: "High-Fidelity Design", desc: "Crafting beautiful Figma pages and interactive mockups before laying a line of code." },
            { title: "Agile Engineering", desc: "Bi-weekly sprints keeping all stakeholders updated on front/back progress transparently." },
            { title: "Continuous Delivery", desc: "Setting up secure development-production pipelines for instant automatic updates." }
        ]
    },
    {
        id: "mobile-app-development",
        title: "Native Mobile Apps",
        url: "/services/mobile-app-development",
        description: "High-performance iOS and Android products built natively or through cross-platform frameworks. Full support from design to app store delivery.",
        icon: FaMobileAlt,
        gradient: 'from-purple-500 to-pink-400',
        iconBg: 'bg-purple-50 text-purple-600 dark:bg-purple-950/30 dark:text-purple-400',
        imageName: '/images/mobile_dev_hero.png',
        tags: ['Flutter & React Native', 'iOS & Android Native', 'APIs & Store Launch'],
        benefits: [
            { icon: FaCode, title: "Hybrid & Native Efficiency", text: "Build once and deploy everywhere using Flutter or React Native for reduced time-to-market." },
            { icon: FaPaintBrush, title: "Polished Performance UX", text: "Butter-smooth animations, touch feedback, and deep native OS hook integrations." },
            { icon: FaLaptopCode, title: "API Integration", text: "Clean connection layers hooking up your app to databases and web services safely." },
            { icon: FaRocket, title: "Store Submission", text: "We manage the entire deployment guidelines on Google Play and Apple App Store." }
        ],
        processSteps: [
            { title: "User Flows", desc: "Mapping layout navigation paths to simplify screen taps and enhance retention." },
            { title: "Prototyping", desc: "Validating user gestures and layouts directly on real devices with testers." },
            { title: "Feature Sprinting", desc: "Iterating elements (push notifications, maps, local caching) rapidly." },
            { title: "Submission & Analytics", desc: "Launching with integrated tracking tools and ongoing performance monitoring." }
        ]
    },
    {
        id: "ecommerce-solutions", // kept id to prevent link breaks
        title: "SaaS Solutions",
        url: "/services/ecommerce-solutions",
        description: "Custom subscription products, multi-tenant dashboards, secure payment flows, and scaling management platforms constructed for scaling founders.",
        icon: FaCloud,
        gradient: 'from-emerald-500 to-teal-400',
        iconBg: 'bg-emerald-50 text-emerald-600 dark:bg-emerald-950/30 dark:text-emerald-400',
        imageName: '/images/ecommerce_hero.png',
        tags: ['Custom Dashboards', 'Stripe Subscriptions', 'Multi-Tenancy Setup'],
        benefits: [
            { icon: FaRocket, title: "Multi-Tenant Design", text: "Secure data separation between accounts while retaining modular system infrastructure." },
            { icon: FaSearch, title: "Billing Integrations", text: "Frictionless setup of subscriptions, custom plans, Stripe billing, and invoicing." },
            { icon: FaLaptopCode, title: "Analytics Dashboards", text: "Rich telemetry charts showing user behavior, MRR, retention, and activity feeds." },
            { icon: FaCode, title: "Open Extensibility", text: "Expose clean APIs for third-party integrations, webhook events, and client expansions." }
        ],
        processSteps: [
            { title: "Systems Planning", desc: "Determining database scalability limits and mapping robust tenant access configurations." },
            { title: "UX Layout Framework", desc: "Designing complex tables, forms, filter sidebars, and settings views intuitively." },
            { title: "Billing & Core Setup", desc: "Developing Stripe subscription logic, trial models, and secure API gateways." },
            { title: "Pre-Flight Security Audits", desc: "Aggressively pen-testing authentication rules and database access layers." }
        ]
    },
    {
        id: "email-services", // kept id to prevent link breaks
        title: "Email Hosting Services",
        url: "/services/email-services",
        description: "Certified Microsoft Partner provisioned corporate email infrastructure. Fast, secure internal mails with complete management and 24/7 support.",
        icon: FaEnvelopeOpenText,
        gradient: 'from-orange-500 to-rose-400',
        iconBg: 'bg-orange-50 text-orange-600 dark:bg-orange-950/30 dark:text-orange-400',
        imageName: '/images/email_hero.png',
        tags: ['Certified MS Vendor', 'SPF/DKIM/DMARC Keys', '24/7 Troubleshooting'],
        benefits: [
            { icon: FaServer, title: "Certified MS Vendor", text: "Official provisioning of Outlook, Exchange, and integrated secure business suites." },
            { icon: FaEnvelopeOpenText, title: "Dedicated Support", text: "24/7 help desk troubleshooting config files, issues, and domain verifications." },
            { icon: FaPaintBrush, title: "Security Measures", text: "Implementing state-of-the-art filters protecting your domains from phishing and malware spoofing." },
            { icon: FaSearch, title: "Deliverability Config", text: "Aggressive setups of SPF, DKIM, and DMARC parameters ensuring inbox deliverability." }
        ],
        processSteps: [
            { title: "Domain Alignment", desc: "Verifying current MX entries and clean setup configuration of domain maps." },
            { title: "Migration Setup", desc: "Safely transfer files, calendar events, and archive mailboxes without data loss." },
            { title: "Identity Provisioning", desc: "Creating global mailbox lists, admin levels, and shared workspace accounts." },
            { title: "Continuous Monitoring", desc: "Monitoring traffic channels, security spikes, and checking reputation health logs daily." }
        ]
    },
    {
        id: "ai-intelligent-agents",
        title: "AI Integration & Agents",
        url: "/services/ai-intelligent-agents",
        description: "Leverage the power of generative AI. Custom LLM tooling, automated workflows, intelligent agents, RAG engines, and vector search systems.",
        icon: FaBrain,
        gradient: 'from-indigo-500 to-purple-400',
        iconBg: 'bg-indigo-50 text-indigo-600 dark:bg-indigo-950/30 dark:text-indigo-400',
        imageName: '/images/ai_hero.png',
        tags: ['LLMs & RAG Engines', 'Vector Database Search', 'Agent Workflows'],
        benefits: [
            { icon: FaBrain, title: "AI-Powered Agents", text: "Automate repetitive customer support or ops workflows using intelligent agents." },
            { icon: FaCode, title: "Retrieval RAG Models", text: "Feed internal documentation to LLMs for highly customized, context-aware replies." },
            { icon: FaLaptopCode, title: "Vector Databases", text: "Perform semantic search across millions of items in milliseconds with Pinecone or pgvector." },
            { icon: FaRocket, title: "Smart Automation", text: "Optimize business loops through AI actions, decreasing processing costs significantly." }
        ],
        processSteps: [
            { title: "Use-Case Evaluation", desc: "Analyzing workflows to define where AI triggers generate the highest efficiency." },
            { title: "Prompt Engineering & RAG", desc: "Configuring system prompts, semantic embeddings, and context window lengths." },
            { title: "Execution Pipeline", desc: "Hooking up LLM requests to backend logic with secure token limits and retries." },
            { title: "Training and Tuning", desc: "Analyzing conversation logs, refining retrieval parameters, and tuning results." }
        ]
    },
    {
        id: "devops-scaling",
        title: "DevOps & Cloud Scaling",
        url: "/services/devops-scaling",
        description: "Zero-downtime automated deployment infrastructures, AWS/GCP optimization, database tuning, and aggressive load testing.",
        icon: FaServer,
        gradient: 'from-red-500 to-orange-400',
        iconBg: 'bg-red-50 text-red-600 dark:bg-red-950/30 dark:text-red-400',
        imageName: '/images/devops_hero.png',
        tags: ['AWS Cloud Setup', 'Docker / CI-CD Flows', 'Query & Cache Tuning'],
        benefits: [
            { icon: FaServer, title: "High-Availability Clusters", text: "Setup auto-scaling container orchestration to survive sudden traffic spikes." },
            { icon: FaRocket, title: "CI/CD Automations", text: "Continuous build, test, and release flows so features reach users instantly and safely." },
            { icon: FaCode, title: "Performance Diagnostics", text: "Database query tuning, caching server integrations, and content delivery (CDN) optimization." },
            { icon: FaSearch, title: "Telemetry & Logs", text: "Real-time resource alerting, log gathering, and uptime monitors tracking health 24/7." }
        ],
        processSteps: [
            { title: "Infrastructure Audit", desc: "Highlighting server bottlenecks, single points of failure, and over-provisioned costs." },
            { title: "Pipeline Configuration", desc: "Scripting GitHub Actions, environment pipelines, and database migrations." },
            { title: "Environment Orchestration", desc: "Deploying secure virtual networks, relational clusters, and redis caches." },
            { title: "Zero-Downtime Rollout", desc: "Final load-testing tests and switching traffic routing methods seamlessly to stable slots." }
        ]
    }
];
